import { useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Sparkles, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatErr } from "../auth/AuthContext";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKS = [
  { key: "credits_10", keyUsd: "credits_10_usd", n: 10, emoji: "", price: "R$ 9,90", priceUsd: "$1.99", tagKey: "buy_tag_start", color: "#FFD500", popular: false },
  { key: "credits_30", keyUsd: "credits_30_usd", n: 30, emoji: "⭐", price: "R$ 24,90", priceUsd: "$4.99", tagKey: "buy_tag_popular", color: "#00FFA3", popular: true },
  { key: "credits_100", keyUsd: "credits_100_usd", n: 100, emoji: "🔥", price: "R$ 69,90", priceUsd: "$13.99", tagKey: "buy_tag_best", color: "#BD00FF", popular: false },
];

export const BuyCreditsModal = ({ onClose, reason }) => {
  const { t, lang } = useLang();
  const isEn = lang === "en";
  const [loading, setLoading] = useState(null);

  const buy = async (displayKey, lookupKey) => {
    setLoading(displayKey);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      // Stripe Checkout não abre dentro de iframe (preview). Se estivermos em iframe,
      // abrimos em nova aba; em produção redirecionamos a própria janela.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        const win = window.open(data.checkout_url, "_blank", "noopener");
        if (!win) {
          toast.error("Permita pop-ups ou abra o app em uma nova aba para pagar.");
        }
        setLoading(null);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erro ao iniciar compra");
      setLoading(null);
    }
  };

  return (
    <div
      data-testid="buy-credits-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white border-[4px] border-black rounded-2xl shadow-brutal-lg p-6"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-bold uppercase text-3xl tracking-tight leading-none">
            {t.buy_title}
          </h2>
          <button data-testid="buy-close" onClick={onClose} className="border-[2px] border-black rounded-lg p-1">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {reason === "limit" ? (
          <p className="text-sm font-semibold text-[#FF3366] mb-1">
            {t.buy_limit}
          </p>
        ) : null}
        <p className="text-sm font-medium text-[#444] mb-5">
          {t.buy_sub}
        </p>

        <div className="flex flex-col gap-3">
          {PACKS.map((p) => (
            <button
              key={p.key}
              data-testid={`buy-${p.key}`}
              onClick={() => buy(p.key, isEn ? p.keyUsd : p.key)}
              disabled={loading}
              style={{ backgroundColor: p.color }}
              className="relative flex items-center justify-between border-[3px] border-black rounded-xl px-5 py-4 shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] disabled:opacity-70"
            >
              {p.popular && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-black rotate-3">
                  {t.buy_popular}
                </span>
              )}
              <span className="flex flex-col items-start">
                <span className="flex items-center gap-2 font-display font-bold uppercase text-xl tracking-tight">
                  <Sparkles size={18} strokeWidth={2.5} /> {p.n} {t.buy_unit}{p.emoji ? ` ${p.emoji}` : ""}
                </span>
                <span className="text-xs font-semibold normal-case opacity-80">{t[p.tagKey]}</span>
              </span>
              <span className="font-mono2 font-bold text-lg flex items-center gap-2">
                {loading === p.key ? <Loader2 size={18} className="animate-spin" /> : (isEn ? p.priceUsd : p.price)}
              </span>
            </button>
          ))}
        </div>

        <p className="mt-2 text-[10px] leading-tight text-[#999] text-center">{t.buy_tax_note}</p>

        <div className="mt-5 flex items-start gap-2 text-xs font-medium text-[#666]">
          <Check size={14} strokeWidth={3} className="mt-0.5 shrink-0" />
          {t.buy_secure}
        </div>
      </motion.div>
    </div>
  );
};
